"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import { ArrowRight } from "lucide-react";
import { projects, categoryFilters, type ProjectCategory } from "@/data/projects";

// Client component that owns the filter chips + grid.
// Filter state lives in the URL (?cat=<slug>) so links are shareable;
// the URL is updated via router.replace with scroll: false so chip clicks
// feel instant (no server roundtrip, no scroll jump).
export function ProjectsFilter() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const raw = searchParams.get("cat");
  const valid = categoryFilters.map((f) => f.slug);
  const activeFilter = (valid.includes(raw as ProjectCategory | "all") ? raw : "all") as
    | ProjectCategory
    | "all";

  const visibleProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  const setFilter = (slug: ProjectCategory | "all") => {
    const url = slug === "all" ? "/projects" : `/projects?cat=${slug}`;
    startTransition(() => {
      router.replace(url, { scroll: false });
    });
  };

  return (
    <>
      {/* Filter strip */}
      <section className="bg-bg border-b border-border">
        <div className="site-container py-6 flex flex-wrap items-center gap-3">
          {categoryFilters.map(({ slug, label }) => {
            const isActive = activeFilter === slug;
            return (
              <button
                key={slug}
                type="button"
                onClick={() => setFilter(slug)}
                aria-pressed={isActive}
                className={[
                  "rounded-pill border px-4 py-1.5 text-small transition-all duration-150",
                  isActive
                    ? "border-accent bg-accent text-accent-ink font-semibold"
                    : "border-border-strong text-text-muted hover:border-accent hover:text-accent",
                ].join(" ")}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Project grid */}
      <section className="bg-bg-soft border-b border-border">
        <div
          className={[
            "site-container py-16 lg:py-20 transition-opacity duration-200",
            isPending ? "opacity-60" : "opacity-100",
          ].join(" ")}
        >
          {visibleProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-h3 text-text-muted">
                No projects in this category yet.
              </p>
              <button
                type="button"
                onClick={() => setFilter("all")}
                className="mt-4 inline-flex items-center gap-1.5 text-small font-semibold text-accent link-anim"
              >
                See all projects
                <ArrowRight className="size-4" />
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visibleProjects.map((p) => {
                const categoryDisplay = categoryFilters.find((f) => f.slug === p.category)?.label ?? p.category;
                return (
                  <Link
                    key={p.slug}
                    href={`/projects/${p.slug}`}
                    className="group rounded-md border border-border bg-bg overflow-hidden transition-all duration-300 hover:border-accent/40 hover:-translate-y-0.5"
                  >
                    <div className="relative aspect-[4/3] bg-gradient-to-br from-bg-elevated via-bg-soft to-bg overflow-hidden">
                      {p.coverImage ? (
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                          style={{ backgroundImage: `url(${p.coverImage})` }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
                          <span className="text-h2 font-extrabold uppercase tracking-tight text-text/20">
                            {categoryDisplay.split(" ")[0]}
                          </span>
                        </div>
                      )}
                      <div
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)",
                        }}
                      />
                      <div aria-hidden="true" className="absolute left-5 top-5 h-px w-10 bg-accent" />
                      <div aria-hidden="true" className="absolute left-5 top-5 w-px h-10 bg-accent" />
                      <div aria-hidden="true" className="absolute bottom-4 right-4 size-1.5 rounded-full bg-accent" />
                    </div>
                    <div className="p-5">
                      <p className="text-small uppercase tracking-wider text-accent">
                        {categoryDisplay}
                      </p>
                      <h3 className="mt-2 text-h3">{p.title}</h3>
                      <p className="mt-2 text-small text-text-muted">{p.scope.join(" · ")}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-small font-semibold text-text group-hover:text-accent">
                        See case study
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <p className="mt-10 text-small text-text-muted">
            Showing {visibleProjects.length} of {projects.length} projects
            {activeFilter !== "all" && (
              <>
                {" "}filtered by{" "}
                <span className="text-accent">
                  {categoryFilters.find((f) => f.slug === activeFilter)?.label}
                </span>
              </>
            )}
            .
          </p>
        </div>
      </section>
    </>
  );
}
